import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { createChurch, updateChurch, deleteChurch } from '@/lib/actions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, Church, Plus, Trash2, DoorOpen, DoorClosed, Bell, Clock, MapPin, Server,
} from 'lucide-react';

type Pos = { x: number; y: number; z: number; name?: string };
type DoorCfg = { mode: 'auto' | 'open' | 'closed'; openAt?: string; closeAt?: string };
type ChurchDoc = {
  id: string;
  name?: string;
  coord?: Pos;
  door?: Pos | null;
  bells?: Pos[];
  bellEnabled?: boolean;
  bellTimes?: string[];
  doorBig?: DoorCfg;
  doorSmall?: DoorCfg;
};

const posKey = (p: Pos) => `${p.x},${p.y},${p.z}`;
const fmtPos = (p?: Pos | null) => (p ? `${p.x}, ${p.y}, ${p.z}` : '——');

export default function AdminIglesiasPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [churches, setChurches] = useState<ChurchDoc[]>([]);
  const [mainServerId, setMainServerId] = useState<string | null>(null);
  const [serverDoors, setServerDoors] = useState<Pos[]>([]);
  const [serverBells, setServerBells] = useState<Pos[]>([]);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  useEffect(() => {
    if (userData?.role !== 'admin') return;
    const u1 = onSnapshot(collection(db, 'mod_churches'), (snap) => {
      setChurches(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
    const u2 = onSnapshot(doc(db, 'mod_config', 'main'), (snap) => {
      setMainServerId(snap.exists() ? (snap.data().mainServerId || null) : null);
    });
    return () => { u1(); u2(); };
  }, [userData?.role]);

  useEffect(() => {
    if (!mainServerId) { setServerDoors([]); setServerBells([]); return; }
    const u = onSnapshot(doc(db, 'mod_servers', mainServerId), (snap) => {
      const d = snap.exists() ? (snap.data() as any) : {};
      setServerDoors(Array.isArray(d.doors) ? d.doors : []);
      setServerBells(Array.isArray(d.bellSwitches) ? d.bellSwitches : []);
    });
    return () => u();
  }, [mainServerId]);

  if (loading || userData?.role !== 'admin') {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-8 pb-12">
      <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-amber-500/15 via-card/40 to-card/20 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="grid h-16 w-16 place-items-center rounded-2xl border border-amber-400/30 bg-amber-500/15 text-amber-300"><Church className="h-9 w-9" /></div>
            <div>
              <h1 className="font-headline text-4xl font-bold md:text-5xl">Gestión de Iglesias</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">Controla puertas y campanas de cada iglesia del servidor principal desde la web.</p>
            </div>
          </div>
          <NewChurchDialog mainServerId={mainServerId} serverDoors={serverDoors} serverBells={serverBells} />
        </div>
      </div>

      {!mainServerId && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-300 flex items-center gap-2">
          <Server className="h-4 w-4" /> No hay servidor principal configurado. Márcalo en <strong>Seguridad del Mod</strong> para poder seleccionar puertas y campanas.
        </div>
      )}

      {churches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
          <Church className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">Aún no hay iglesias. Crea la primera.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {churches.map((c) => <ChurchCard key={c.id} church={c} serverBells={serverBells} />)}
        </div>
      )}
    </div>
  );
}

function NewChurchDialog({ mainServerId, serverDoors, serverBells }: { mainServerId: string | null; serverDoors: Pos[]; serverBells: Pos[] }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [coord, setCoord] = useState({ x: '', y: '', z: '' });
  const [doorKey, setDoorKey] = useState<string>('');
  const [bellKeys, setBellKeys] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const toggleBell = (k: string) => setBellKeys((prev) => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n;
  });

  const handleCreate = async () => {
    if (!name.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    setSaving(true);
    const door = serverDoors.find((d) => posKey(d) === doorKey) || null;
    const bells = serverBells.filter((b) => bellKeys.has(posKey(b)));
    const res = await createChurch({
      name: name.trim(),
      coord: { x: Number(coord.x) || 0, y: Number(coord.y) || 0, z: Number(coord.z) || 0 },
      door: door ? { x: door.x, y: door.y, z: door.z, name: door.name || '' } : null,
      bells: bells.map((b) => ({ x: b.x, y: b.y, z: b.z })),
      bellEnabled: false,
      bellTimes: ['12:00', '20:00'],
      doorBig: { mode: 'closed' },
      doorSmall: { mode: 'closed' },
    });
    if (res.success) {
      toast({ title: 'Iglesia creada', description: res.message });
      setOpen(false); setName(''); setCoord({ x: '', y: '', z: '' }); setDoorKey(''); setBellKeys(new Set());
    } else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" disabled={!mainServerId}><Plus className="h-4 w-4" /> Crear iglesia</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6"><DialogTitle>Nueva iglesia</DialogTitle><DialogDescription>Coloca la iglesia y enlázala con su puerta y sus campanas del servidor principal.</DialogDescription></DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-2 space-y-4">
          <div className="space-y-1.5"><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Basílica de la Macarena" /></div>
          <div className="space-y-1.5">
            <Label>Coordenada de la iglesia (X / Y / Z)</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input type="number" placeholder="X" value={coord.x} onChange={(e) => setCoord({ ...coord, x: e.target.value })} />
              <Input type="number" placeholder="Y" value={coord.y} onChange={(e) => setCoord({ ...coord, y: e.target.value })} />
              <Input type="number" placeholder="Z" value={coord.z} onChange={(e) => setCoord({ ...coord, z: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Puerta de la iglesia</Label>
            <Select value={doorKey} onValueChange={setDoorKey}>
              <SelectTrigger><SelectValue placeholder={serverDoors.length ? 'Selecciona una puerta…' : 'No hay puertas detectadas'} /></SelectTrigger>
              <SelectContent>
                {serverDoors.map((d) => (
                  <SelectItem key={posKey(d)} value={posKey(d)}>{d.name || 'puerta'} · {fmtPos(d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Campanas de la iglesia (interruptores)</Label>
            {serverBells.length === 0 ? (
              <p className="text-xs text-muted-foreground">No hay interruptores de campanas detectados en el servidor.</p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto rounded-xl border border-white/8 p-2">
                {serverBells.map((b) => {
                  const k = posKey(b);
                  return (
                    <label key={k} className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-muted/40 cursor-pointer text-sm">
                      <input type="checkbox" checked={bellKeys.has(k)} onChange={() => toggleBell(k)} />
                      <Bell className="h-3.5 w-3.5 text-amber-400" /> Interruptor · {fmtPos(b)}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="px-6 pb-6 pt-2">
          <Button onClick={handleCreate} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crear iglesia</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChurchCard({ church, serverBells }: { church: ChurchDoc; serverBells: Pos[] }) {
  const { toast } = useToast();
  const big = church.doorBig || { mode: 'closed' as const };
  const small = church.doorSmall || { mode: 'closed' as const };
  const [bigSched, setBigSched] = useState({ openAt: big.openAt || '09:00', closeAt: big.closeAt || '21:00' });
  const [smallSched, setSmallSched] = useState({ openAt: small.openAt || '19:00', closeAt: small.closeAt || '20:00' });
  const [times, setTimes] = useState((church.bellTimes || ['12:00', '20:00']).join(', '));

  const save = async (updates: any) => {
    const res = await updateChurch(church.id, updates);
    if (!res.success) toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };

  const DoorRow = ({ label, cfg, sched, setSched, field }: { label: string; cfg: DoorCfg; sched: { openAt: string; closeAt: string }; setSched: (s: { openAt: string; closeAt: string }) => void; field: 'doorBig' | 'doorSmall' }) => (
    <div className="rounded-xl border border-white/8 bg-muted/20 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex gap-1">
          {(['open', 'closed', 'auto'] as const).map((m) => (
            <Button key={m} size="sm" variant={cfg.mode === m ? 'default' : 'secondary'}
              className="h-7 px-2 text-xs"
              onClick={() => save({ [field]: { ...cfg, mode: m, openAt: sched.openAt, closeAt: sched.closeAt } })}>
              {m === 'open' ? <DoorOpen className="h-3.5 w-3.5" /> : m === 'closed' ? <DoorClosed className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
              <span className="ml-1">{m === 'open' ? 'Abrir' : m === 'closed' ? 'Cerrar' : 'Auto'}</span>
            </Button>
          ))}
        </div>
      </div>
      {cfg.mode === 'auto' && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Abre</span>
          <Input type="time" value={sched.openAt} onChange={(e) => setSched({ ...sched, openAt: e.target.value })} onBlur={() => save({ [field]: { mode: 'auto', openAt: sched.openAt, closeAt: sched.closeAt } })} className="h-8 w-28" />
          <span className="text-muted-foreground">Cierra</span>
          <Input type="time" value={sched.closeAt} onChange={(e) => setSched({ ...sched, closeAt: e.target.value })} onBlur={() => save({ [field]: { mode: 'auto', openAt: sched.openAt, closeAt: sched.closeAt } })} className="h-8 w-28" />
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Church className="h-5 w-5 text-amber-400" />
          <div>
            <p className="font-semibold">{church.name || 'Iglesia'}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {fmtPos(church.coord)}</p>
          </div>
        </div>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10"
          onClick={async () => { if (confirm(`¿Eliminar "${church.name}"?`)) { const r = await deleteChurch(church.id); if (!r.success) toast({ title: 'Error', description: r.message, variant: 'destructive' }); } }}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">Puerta: {church.door ? `${church.door.name || 'puerta'} · ${fmtPos(church.door)}` : 'sin asignar'}</p>

      {/* Puertas */}
      <DoorRow label="Puertas grandes" cfg={big} sched={bigSched} setSched={setBigSched} field="doorBig" />
      <DoorRow label="Puertas chicas" cfg={small} sched={smallSched} setSched={setSmallSched} field="doorSmall" />

      {/* Campanas */}
      <div className="rounded-xl border border-white/8 bg-muted/20 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium flex items-center gap-1.5"><Bell className="h-4 w-4 text-amber-400" /> Campanas ({(church.bells || []).length})</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Toque automático</span>
            <Switch checked={church.bellEnabled === true} onCheckedChange={(v) => save({ bellEnabled: v })} />
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Horas (1 min c/u)</span>
          <Input value={times} onChange={(e) => setTimes(e.target.value)}
            onBlur={() => save({ bellTimes: times.split(',').map((t) => t.trim()).filter(Boolean) })}
            placeholder="12:00, 20:00" className="h-8" />
        </div>
      </div>
    </div>
  );
}
